// Regras e formatação do formulário público de cadastro de médico.
//
// As mesmas regras existem no servidor (laudario_api/accounts/validators.py).
// Aqui elas servem para orientar quem digita; lá, para valer de fato, porque o
// endpoint é público e a validação da tela é contornável.
//
// Convenção: o ESTADO do formulário guarda só dígitos nos campos numéricos, e a
// máscara é aplicada apenas na exibição. Assim o envio já sai limpo, e dados de
// cadastros antigos (com ou sem pontuação) são normalizados ao carregar.

export const IDADE_MINIMA = 25
export const IDADE_MAXIMA = 80

export function somenteDigitos(valor, maximo) {
  const digitos = String(valor || '').replace(/\D/g, '')
  return maximo ? digitos.slice(0, maximo) : digitos
}

// ------------------------------------------------------------------ máscaras

export function formatarCpf(valor) {
  const d = somenteDigitos(valor, 11)
  if (d.length <= 3) return d
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`
}

export function formatarCep(valor) {
  const d = somenteDigitos(valor, 8)
  return d.length <= 5 ? d : `${d.slice(0, 5)}-${d.slice(5)}`
}

export function formatarCelular(valor) {
  const d = somenteDigitos(valor, 11)
  if (d.length === 0) return ''
  if (d.length <= 2) return `(${d}`
  if (d.length <= 7) return `(${d.slice(0, 2)}) ${d.slice(2)}`
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`
}

// -------------------------------------------------------------------- CPF

export function cpfValido(valor) {
  const cpf = somenteDigitos(valor)
  if (cpf.length !== 11) return false
  // sequências como 111.111.111-11 passam no cálculo, mas não existem
  if (/^(\d)\1{10}$/.test(cpf)) return false
  for (const tamanho of [9, 10]) {
    let soma = 0
    for (let i = 0; i < tamanho; i++) {
      soma += Number(cpf[i]) * (tamanho + 1 - i)
    }
    const resto = (soma * 10) % 11
    const digito = resto === 10 ? 0 : resto
    if (digito !== Number(cpf[tamanho])) return false
  }
  return true
}

// Mensagem para exibir ENQUANTO digita: nada até completar os 11 números,
// para não acusar erro no meio da digitação.
export function erroCpf(valor) {
  const d = somenteDigitos(valor)
  if (d.length < 11) return ''
  return cpfValido(d) ? '' : 'CPF inválido. Confira os números.'
}

// ------------------------------------------------------------------ letras

// Letras, inclusive acentuadas. Faixas Latin-1 sem × (U+00D7) e ÷ (U+00F7).
const LETRA = 'A-Za-zÀ-ÖØ-öø-ÿ'
const SO_LETRAS = new RegExp(`^[${LETRA}]+(?:[ '’-][${LETRA}]+)*$`)
const FORA_DE_LETRAS = new RegExp(`[^${LETRA} '’-]`, 'g')

// Para o onChange: descarta o que nunca seria aceito (dígitos, símbolos), mas
// mantém espaço e hífen, necessários enquanto a pessoa ainda está digitando.
export function filtrarLetras(valor) {
  return String(valor || '').replace(FORA_DE_LETRAS, '')
}

export function normalizarEspacos(valor) {
  return String(valor || '').trim().replace(/\s+/g, ' ')
}

export function soLetras(valor) {
  const v = normalizarEspacos(valor)
  return v.length > 0 && SO_LETRAS.test(v)
}

// ------------------------------------------------------------------- idade

function hojeLocal() {
  const agora = new Date()
  return { ano: agora.getFullYear(), mes: agora.getMonth() + 1, dia: agora.getDate() }
}

export function idadeEm(dataIso, referencia) {
  const [ano, mes, dia] = String(dataIso).split('-').map(Number)
  const ref = referencia || hojeLocal()
  let idade = ref.ano - ano
  if (ref.mes < mes || (ref.mes === mes && ref.dia < dia)) idade -= 1
  return idade
}

export function idadeAceita(dataIso, referencia) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(dataIso || ''))) return false
  const idade = idadeEm(dataIso, referencia)
  return idade >= IDADE_MINIMA && idade <= IDADE_MAXIMA
}

function isoDe(ano, mes, dia) {
  const p = n => String(n).padStart(2, '0')
  return `${ano}-${p(mes)}-${p(dia)}`
}

// Limites para o seletor de data: o mais recente é quem faz 25 anos hoje; o
// mais antigo, quem completa 81 amanhã (ainda tem 80). Em 29/02 o ajuste de
// dia é tolerado -- a regra definitiva é idadeAceita.
export function limitesNascimento(referencia) {
  const ref = referencia || hojeLocal()
  const maisRecente = isoDe(ref.ano - IDADE_MINIMA, ref.mes, ref.dia)
  const d = new Date(ref.ano - IDADE_MAXIMA - 1, ref.mes - 1, ref.dia + 1)
  const maisAntigo = isoDe(d.getFullYear(), d.getMonth() + 1, d.getDate())
  return { min: maisAntigo, max: maisRecente }
}
